import React, { useCallback, useMemo } from 'react';
import { CookieConsentData, CookieModal, CookieBannerComplete } from '@cvent/cookieconsent';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import { Z_INDEX } from '@components/shared/zIndex';
import { CSSObject } from '@emotion/react';
import { makeVar, useReactiveVar } from '@apollo/client';

interface CookieConsentProps {
  eventId: string;
  showCookieBanner: boolean;
  showNonEssentialCookieBanner: boolean;
  hidePrivacyPolicyLink?: boolean;
}

export enum BannerTypes {
  ACCEPT = 'accept',
  CUSTOMIZE = 'customize'
}

export const TEST_ID = 'cookie-banner';

export const ACCEPT_ALL: CookieConsentData = {
  Performance: true,
  Functionality: true,
  Marketing: true
};

export const ACCEPT_ESSENTIAL: CookieConsentData = {
  Performance: false,
  Functionality: false,
  Marketing: false
};

export const COOKIE_KEY_PREFIX = 'cvt_cookie_consent-';

const styles: Record<string, CSSObject> = {
  cookieConsentModal: {
    zIndex: Z_INDEX.FLY_OUT,
    position: 'relative'
  }
};

const cookieConsentVar = makeVar(ACCEPT_ESSENTIAL);

/**
 * Hook to get cookie consent values and setter function
 */
export const useCookieConsentVar = () => {
  const cookieConsent = useReactiveVar(cookieConsentVar);

  const setCookieConsent = useCallback(
    (choices: CookieConsentData) => {
      if (
        cookieConsent.Functionality !== choices.Functionality ||
        cookieConsent.Marketing !== choices.Marketing ||
        cookieConsent.Performance !== choices.Performance
      ) {
        cookieConsentVar({ ...choices });
      }
    },
    [cookieConsent]
  );

  return { cookieConsent, setCookieConsent };
};

/**
 * Checks the localstorage to see if any consent has been given for the event,
 * return true if the user has accepted any or all of the cookies based on localstorage
 */
export function getConsentChoices(eventId: string): CookieConsentData | false {
  if (typeof window !== 'undefined') {
    const consentKey = `${COOKIE_KEY_PREFIX}${eventId}`;
    const consentChoicesValue = localStorage.getItem(consentKey);
    try {
      const choices = JSON.parse(consentChoicesValue);
      return choices;
    } catch (jsonParseError) {
      localStorage.removeItem(consentKey);
      return false;
    }
  }
  return false;
}

/**
 * Checks whether all cookie consent choices have been selected
 */
export function checkAllCookieConsent(eventId: string) {
  const choices = getConsentChoices(eventId);

  if (choices) {
    return choices?.Performance && choices?.Functionality && choices?.Marketing;
  }

  return false;
}

export function chooseBannerType(showCustomize: boolean): BannerTypes {
  return showCustomize ? BannerTypes.CUSTOMIZE : BannerTypes.ACCEPT;
}

function CookieConsentBanner({
  eventId,
  showNonEssentialCookieBanner,
  hidePrivacyPolicyLink,
  showCookieBanner
}: CookieConsentProps): JSX.Element {
  const hasAnsweredConsent = useMemo(() => Boolean(getConsentChoices(eventId)), [eventId]);
  const bannerType = useMemo(() => chooseBannerType(showNonEssentialCookieBanner), [showNonEssentialCookieBanner]);

  if (!showCookieBanner || showNonEssentialCookieBanner === undefined) {
    return null;
  }

  return (
    !hasAnsweredConsent && (
      <MoodThemeProvider>
        <CookieBannerComplete
          testID={`${bannerType}-${TEST_ID}`}
          bannerType={bannerType}
          hidePrivacyPolicyLink={hidePrivacyPolicyLink}
        />
      </MoodThemeProvider>
    )
  );
}

function CookieConsentBannerWithModal({
  eventId,
  showNonEssentialCookieBanner,
  showCookieBanner,
  hidePrivacyPolicyLink = false
}: CookieConsentProps): JSX.Element {
  return (
    <>
      <CookieConsentBanner
        eventId={eventId}
        showNonEssentialCookieBanner={showNonEssentialCookieBanner}
        hidePrivacyPolicyLink={hidePrivacyPolicyLink}
        showCookieBanner={showCookieBanner}
      />
      <div css={styles.cookieConsentModal}>
        <CookieModal hidePrivacyPolicyLink={hidePrivacyPolicyLink} applyConsumerHeadingFont />
      </div>
    </>
  );
}

export default CookieConsentBannerWithModal;
